<template lang="pug">
  div(class="organizations-users-notifications")
    h2(class="title-32px fw-700 mb-2") {{ $t('Notifications settings') }}
    div(class="d-flex align-start justify-space-between")
      div(class="text-14px fw-300") {{ $t('All changes are saved automatically. You will start receiving notifications according to the new settings') }}
      div(class="organizations-users-notifications__active-label text-12px") {{ activeCount }}/{{ totalCount }} {{ $t('actives') }}
    div(class="organizations-users-notifications__list")
      div(class="organizations-users-notifications__list-header")
        div(class="organizations-users-notifications__list-header-left")
          div(class="text-16px fw-500 dark") {{ $t('Manage notification types') }}
          div(class="text-12px fw-300") {{ notificationText }}
        div(class="organizations-users-notifications__list-header-right")
          v-btn(
            class="secondary-button xs"
            @click="toggleAllNotifications"
          ) {{ tooggleAllButtonText }}
      div(
        v-for="(settings, idx) in notificationSettings"
        :key="idx"
        class="organizations-users-notifications__list-item"
      )
        div
          div(class="text-14px fw-300 dark") {{ settings.title }}
          div(class="text-12px fw-300") {{ settings.text }}
        div
          v-switch(
            :input-value="form[settings.value]"
            @change="handleChange(settings.value, $event)"
            :loading="loadings[settings.value]"
            class="mt-0 py-0"
            hide-details
          )
</template>

<script>
import OrganizationUsers from '~/modules/organizations/models/OrganizationUsers';
import successSnackbar from '~/modules/snackbar/mixins/successSnackbar'
export default {
  name: 'FormOrganizationsUsersNotifications',
  mixins: [successSnackbar],
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadings: {
        notifyIncoming: false,
        notifySigned: false,
        notifyApproved: false,
        notifyRejected: false,
        notifyComments: false,
        notifyReminders: false
      },
      form: {
        notifyIncoming: false,
        notifySigned: false,
        notifyApproved: false,
        notifyRejected: false,
        notifyComments: false,
        notifyReminders: false
      },
      isFormSetCompleted: false,
      toggleAllUpdate: false
    }
  },
  computed: {
    tooggleAllButtonText() {
      return this.activeCount === this.totalCount ? this.$t('Turn off all') : this.$t('Turn on all')
    },
    totalCount() {
      return Object.keys(this.form).length
    },
    activeCount() {
      return Object.keys(this.form).filter(key => this.form[key]).length
    },
    notificationText() {
      return this.$t('Which ones would you like to receive for {name}', {
        name: this._.get(this.user, 'user.name') || this._.get(this.user, 'user.email')
      })
    },
    notificationSettings() {
      const settings = [
        {
          title: this.$t('Signed documents'),
          text: this.$t('Notifications about documents signed'),
          value: 'notifySigned'
        },
        {
          title: this.$t('Approved documents'),
          text: this.$t('Notifications about documents approved'),
          value: 'notifyApproved'
        },
        {
          title: this.$t('Rejected documents'),
          text: this.$t('Notifications about documents rejected'),
          value: 'notifyRejected'
        },
        {
          title: this.$t('Comments on documents'),
          text: this.$t('Notifications about new comments on your documents'),
          value: 'notifyComments'
        },
        {
          title: this.$t('Uncompleted actions'),
          text: this.$t('Notifications about uncompleted actions'),
          value: 'notifyReminders'
        }
      ]
      if (this.user.role !== OrganizationUsers.ROLES.ACCOUNTANT) {
        settings.unshift({
          title: this.$t('Inbox documents'),
          text: this.$t('Notifications about new documents received'),
          value: 'notifyIncoming'
        })
      }
      return settings
    }
  },
  watch: {
    form: {
      handler(newVal, oldVal) {
        if (!this.isFormSetCompleted || this.toggleAllUpdate) return
        if (!oldVal || Object.keys(oldVal).length === 0) return
        const diffProperty = this._.pickBy(
          newVal,
          (value, key) => !this._.isEqual(value, oldVal[key])
        )
        const keyName = Object.keys(diffProperty)[0]
        if (keyName) {
          this.updateNotifications(newVal, keyName)
        }
      },
      deep: true
    }
  },
  created() {
    this.setForm()
  },
  methods: {
    setForm() {
      this.form = {
        notifyIncoming: this._.get(this.user, 'notifyIncoming', false),
        notifySigned: this._.get(this.user, 'notifySigned', false),
        notifyApproved: this._.get(this.user, 'notifyApproved', false),
        notifyRejected: this._.get(this.user, 'notifyRejected', false),
        notifyComments: this._.get(this.user, 'notifyComments', false),
        notifyReminders: this._.get(this.user, 'notifyReminders', false)
      }
      this.$nextTick(() => {
        this.isFormSetCompleted = true
      })
    },
    async updateNotifications(form, keyName) {
      try {
        this.loadings[keyName] = true
        await this.$store.dispatch('organizationusers/notify', {
          id: this.user.id,
          payload: form
        })
        await this.$store.dispatch('organizationusers/getAll', {
          organizationId: this.$route.params.id,
          config: {
            params: this.$store.getters['organizations/requestParams']
          }
        })
        await this.successSnackbar.open({
          text: this.$t('Changes have been saved automatically')
        })
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.loadings[keyName] = false
      }
    },
    setLoadings(value) {
      Object.keys(this.loadings).forEach(key => {
        this.loadings[key] = value
      })
    },
    setFormAfterUpdateAllNotifications(value) {
      this.form = {
        notifyIncoming: value,
        notifySigned: value,
        notifyApproved: value,
        notifyRejected: value,
        notifyComments: value,
        notifyReminders: value
      }
    },
    async toggleAllNotifications() {
      try {
        this.toggleAllUpdate = true
        const value = this.activeCount === this.totalCount
        this.setLoadings(true)
        await this.$store.dispatch('organizationusers/notify', {
          id: this.user.id,
          payload: {
            notifyIncoming: !value,
            notifySigned: !value,
            notifyApproved: !value,
            notifyRejected: !value,
            notifyComments: !value,
            notifyReminders: !value
          }
        })
        this.setFormAfterUpdateAllNotifications(!value)
        await this.$store.dispatch('organizationusers/getAll', {
          organizationId: this.$route.params.id,
          config: {
            params: this.$store.getters['organizations/requestParams']
          }
        })
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.setLoadings(false)
        this.toggleAllUpdate = false
      }
    },
    handleChange(value, event) {
      this.form = {
        ...this.form,
        [value]: event
      }
    }
  }
}
</script>

<style scoped lang="scss">
.organizations-users-notifications {
  &__active-label {
    background-color: $btn-secondary-text-hover;
    color: $primary;
    padding: 4px 8px;
    border-radius: 20px;
    white-space: nowrap;
  }
  &__list {
    margin-top: 32px;
    border-radius: 16px;
    background: $background-gray;
    padding: 16px;
  }
  &__list-header {
    display: flex;
    justify-content: space-between;
    padding-bottom: 16px;
    border-bottom: 1px solid $border-gray;
    margin-bottom: 24px;
  }
  &__list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:not(:last-child) {
      margin-bottom: 24px;
    }
  }
}
</style>
